/**
 * SideQuestsList Component
 *
 * Renders the side quests sidebar panel with real data from the store.
 * Each quest card shows its status (available, completed, expired),
 * points, and deadline when applicable.
 */

import React from 'react'
import { observer } from 'mobx-react-lite'
import { MDBCard, MDBCardBody, MDBIcon, MDBBtn } from 'mdb-react-ui-kit'
import Store from '../../FirstSaleTracker.store'

import './SideQuestsList.scss'

const CATEGORY_CONFIG = {
  speed_runs: {
    label: 'Speed Runs',
    icon: 'stopwatch',
  },
  consistency_streaks: {
    label: 'Consistency Streaks',
    icon: 'fire',
  },
  learning_unlocks: {
    label: 'Learning Unlocks',
    icon: 'graduation-cap',
  },
  bonus_achievements: {
    label: 'Bonus Achievements',
    icon: 'award',
  },
  get_the_word_out: {
    label: 'Get The Word Out',
    icon: 'bullhorn',
  },
  call_appt_milestones: {
    label: 'Call & Appt Milestones',
    icon: 'phone-alt',
  },
}

/** Tier definitions for tooltip display on tiered quests. */
const TIER_TOOLTIPS = {
  call_milestones: [
    { label: 'Dialer', requirement: '20 total calls' },
    { label: 'Hot Streak', requirement: '50 calls in a single day' },
    { label: 'Call Machine', requirement: '100 calls in a single day' },
    { label: 'Phone Warrior', requirement: '200 calls in a single day' },
    { label: 'Legendary Dialer', requirement: '300 calls in a single day' },
    { label: 'Call God', requirement: '500 calls in a single day' },
  ],
  appt_milestones: [
    { label: 'Appointment Starter', requirement: '3 total appointments' },
    { label: 'Appointment Setter', requirement: '5 total appointments' },
    { label: 'Appointment Pro', requirement: '10 total appointments' },
    { label: 'Appointment Master', requirement: '20 total appointments' },
    { label: 'Appointment Legend', requirement: '50 total appointments' },
  ],
}

/** Zero-progress descriptions per tiered quest key. */
const TIERED_ZERO_DESC = {
  call_milestones: 'Make calls to start leveling up!',
  appt_milestones: 'Set appointments to start leveling up!',
}

/** Short descriptions for card display (keeps cards compact). */
const SHORT_DESC = {
  speed_closer: 'First sale within 7 days',
  speed_run_first_three: 'Steps 1-3 within 3 days',
  three_day_call_streak: '5+ calls/day, 3 days in a row',
  weekly_hustler: '2+ appointments set',
  carrier_training_attendance: 'Attend a carrier training',
  case_study_attendance: 'Attend a case study event',
  deep_dive_attendance: 'Attend a deep dive study',
  referral_booster: 'Attach a referral partner to a sale',
  multi_carrier_debut: '2+ different carriers',
  state_hopper: 'Sales in 2+ different states',
  multi_state_licensed: 'Licensed in 2+ states',
  goal_setter: 'Set at least one KPI goal',
  first_cross_sell: '2+ policies for same client',
  setup_agent_subsite: 'Set up your agent website',
  share_on_social_media: 'Share on social media',
}

const SideQuestCard = ({ quest, onClaim }) => {
  const { label, description, icon, points, status, deadline_at, meta, progress, goal, detection_type, quest_key } =
    quest
  const isTiered = goal != null && progress != null

  const hasProgress = isTiered && progress > 0
  const stateClass =
    status === 'completed'
      ? 'side-quests__card--completed'
      : hasProgress
      ? 'side-quests__card--completed'
      : status === 'locked'
      ? 'side-quests__card--locked'
      : ''

  const formattedDeadline =
    deadline_at && status === 'available'
      ? new Date(deadline_at + 'T12:00:00').toLocaleDateString('en-US', {
          month: 'long',
          day: 'numeric',
        })
      : null

  const completedValue =
    !isTiered && status === 'completed' && meta && meta.days_elapsed != null
      ? `${meta.days_elapsed} day${meta.days_elapsed !== 1 ? 's' : ''}`
      : null

  // Tiered quest display
  const tierLabel = isTiered && meta && meta.current_tier ? meta.current_tier.label : null
  const tierDesc = isTiered && meta && meta.current_tier ? meta.current_tier.desc : null
  const tierTooltip = TIER_TOOLTIPS[quest_key] || null

  return (
    <MDBCard className={`side-quests__card ${stateClass}`}>
      <MDBCardBody className='side-quests__card-body'>
        <div className='side-quests__card-icon'>
          <MDBIcon icon={icon} />
        </div>
        <div className='side-quests__card-content'>
          <div className='side-quests__card-title-row'>
            <strong>{isTiered && tierLabel ? tierLabel : label}</strong>
            {tierTooltip && (
              <span className='side-quests__tier-info'>
                <MDBIcon icon='info-circle' />
                <span className='side-quests__tier-tooltip-content'>
                  <strong>{label}</strong>
                  <ul>
                    {tierTooltip.map((t, i) => (
                      <li key={i}>
                        <span className='side-quests__tier-tooltip-label'>{t.label}</span>
                        <span className='side-quests__tier-tooltip-req'>{t.requirement}</span>
                      </li>
                    ))}
                  </ul>
                </span>
              </span>
            )}
          </div>
          <small>
            {isTiered
              ? tierDesc || (progress === 0 ? TIERED_ZERO_DESC[quest_key] || description : description)
              : SHORT_DESC[quest.quest_key] || description}
          </small>
          {isTiered && (
            <span className='side-quests__card-level'>
              Level {progress} / {goal}
            </span>
          )}
          {completedValue && <span className='side-quests__card-result'>Completed in {completedValue}</span>}
          {formattedDeadline && <span className='side-quests__card-deadline'>Deadline: {formattedDeadline}</span>}
          {status === 'locked' && <span className='side-quests__card-expired-label'>Expired</span>}
        </div>
        <div className='side-quests__card-pts'>+{points} pts</div>
      </MDBCardBody>
      {detection_type === 'manual' && status === 'available' && onClaim && (
        <div className='side-quests__card-claim'>
          <MDBBtn type='button' size='sm' color='success' outline onClick={() => onClaim(quest_key)}>
            Mark Complete
          </MDBBtn>
        </div>
      )}
    </MDBCard>
  )
}

const SideQuestsList = () => {
  const { sideQuests } = Store
  const { quests, earnedPoints, totalPoints } = sideQuests

  if (!quests || !quests.length) return null

  // Group quests by category
  const categories = {}
  quests.forEach(q => {
    if (!categories[q.category]) categories[q.category] = []
    categories[q.category].push(q)
  })

  return (
    <div className='side-quests'>
      <div className='side-quests__banner'>
        <MDBIcon icon='scroll' className='mr-2' />
        Sales Tracker — Side Quests
      </div>

      <div className='side-quests__header'>
        <MDBIcon icon='bolt' className='side-quests__header-icon' />
        <div>
          <h6 className='side-quests__title'>Side Quests</h6>
          <p className='side-quests__subtitle'>Earn extra points by completing bonus challenges.</p>
        </div>
      </div>

      <div className='side-quests__points-summary'>
        <MDBIcon icon='star' className='mr-1' />
        <span>
          {earnedPoints} / {totalPoints} pts earned
        </span>
      </div>

      {Object.entries(categories).map(([catKey, catQuests]) => {
        const config = CATEGORY_CONFIG[catKey] || {
          label: catKey,
          icon: 'star',
        }

        return (
          <div key={catKey} className='side-quests__category'>
            <div className='side-quests__category-header'>
              <MDBIcon icon={config.icon} className='mr-1' />
              <span>{config.label}</span>
            </div>
            <div className='side-quests__grid'>
              {catQuests.map(quest => (
                <SideQuestCard key={quest.quest_key} quest={quest} onClaim={Store.claimQuest} />
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default observer(SideQuestsList)
